import React, { useEffect, useRef, useState } from 'react';

/** Resize in place: moving a player into a portal would reload its iframe. */
export default function ExpandableRevealMedia({ children, label, image = false, className = '' }: {
  children: React.ReactNode;
  label: string;
  image?: boolean;
  className?: string;
}) {
  const [mode, setMode] = useState<'closed' | 'hover' | 'pinned'>('closed');
  const [rotation, setRotation] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressHover = useRef(false);
  const expanded = mode !== 'closed';
  const cancelHover = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const close = () => {
    cancelHover();
    suppressHover.current = true;
    setMode('closed');
  };

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useEffect(() => {
    if (!expanded || !root.current) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const restoreTrigger = trigger.current;
    const shell = root.current.closest<HTMLElement>('.cd-reveal-shell');
    const previousOverflow = shell?.style.overflow || '';
    if (shell) shell.style.overflow = 'hidden';
    const inertSiblings: HTMLElement[] = [];
    // Make everything outside this viewer inert, including the reveal close button.
    let node: HTMLElement = root.current;
    while (node.parentElement && node !== document.body) {
      Array.from(node.parentElement.children).forEach((sibling) => {
        if (sibling !== node && sibling instanceof HTMLElement && !sibling.hasAttribute('inert')) {
          sibling.setAttribute('inert', '');
          inertSiblings.push(sibling);
        }
      });
      node = node.parentElement;
    }
    closeButton.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        suppressHover.current = true;
        setMode('closed');
      }
      if (event.key === 'Tab') {
        const elements = Array.from(root.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], iframe, video[controls], [tabindex="0"]'
        ) || []).filter((element) => !element.hidden && element.getClientRects().length > 0);
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (shell) shell.style.overflow = previousOverflow;
      inertSiblings.forEach((element) => element.removeAttribute('inert'));
      if (previousFocus?.isConnected && previousFocus !== document.body) previousFocus.focus({ preventScroll: true });
      else restoreTrigger?.focus({ preventScroll: true });
    };
  }, [expanded]);

  return <div className={`cd-expand-media ${className} ${expanded ? 'is-expanded' : ''}`} ref={root}
    role={expanded ? 'dialog' : undefined} aria-modal={expanded ? true : undefined}
    aria-label={expanded ? label : undefined}
    onClick={(event) => { if (event.target === event.currentTarget && expanded) close(); }}>
    <div className="cd-expand-panel" onPointerLeave={() => { if (mode === 'hover') close(); }}>
      <div className="cd-expand-toolbar" hidden={!expanded}>
        <strong>{label}</strong>
        {mode === 'hover' && <button type="button" onClick={() => setMode('pinned')}>Keep open</button>}
        {image && <button type="button" onClick={() => { setMode('pinned'); setRotation((value) => (value + 90) % 360); }}>Rotate ↻</button>}
        <button type="button" ref={closeButton} onClick={close} aria-label={`Close expanded ${label}`}>Close ×</button>
      </div>
      <div className={`cd-expand-content ${image ? 'is-image' : ''} ${expanded && rotation % 180 ? 'is-turned' : ''}`}
        style={image ? { '--reveal-rotation': `${expanded ? rotation : 0}deg` } as React.CSSProperties : undefined}
        onPointerEnter={(event) => {
          if (image && event.pointerType === 'mouse' && !expanded && !suppressHover.current) {
            cancelHover();
            timer.current = setTimeout(() => setMode('hover'), 500);
          }
        }}
        onPointerLeave={() => { cancelHover(); suppressHover.current = false; }}
        onClick={image ? () => { cancelHover(); setMode('pinned'); } : undefined}>
        {children}
      </div>
      <button className="cd-expand-trigger" ref={trigger} hidden={expanded} type="button"
        aria-label={`Expand ${label}`} onClick={() => { cancelHover(); setMode('pinned'); }}>⛶ Expand</button>
    </div>
  </div>;
}
